import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';

import {
  DRIVING_LICENSE_CHOICES,
  FRENCH_LEVEL_CHOICES,
} from '../../../helpers';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      marginTop: 16,
    },
    field: {
      marginBottom: theme.spacing(1),
    },
    label: {
      color: theme.palette.text.secondary,
    },
    value: {
      fontWeight: 500,
    },
    section: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: theme.palette.text.secondary,
    },
    sectionFirst: {
      marginTop: theme.spacing(0.5),
    },
    dialogSection: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    yes: {
      color: theme.palette.success ? theme.palette.success.main : '#2e7d32',
      fontWeight: 600,
    },
    no: {
      color: theme.palette.text.disabled,
    },
  };
});

const emptyProfile = (silaeId, sourceCleanerId) => {
  return {
    silae_id: silaeId,
    desired_commute_minutes: null,
    min_weekly_hours_wish: null,
    wants_cdd: false,
    wants_cdi: false,
    experience_years: null,
    driving_license: null,
    french_spoken: null,
    french_written: null,
    email_can_open: false,
    email_can_find: false,
    email_can_write: false,
    is_student: false,
    source_cleaner_id: sourceCleanerId || null,
  };
};

const Display = ({ label, value, classes }) => {
  return (
    <Grid item xs={6} sm={4} className={classes.field}>
      <Typography variant="caption" className={classes.label} display="block">
        {label}
      </Typography>
      <Typography variant="body2" className={classes.value}>
        {value === null || value === undefined || value === ''
          ? '—'
          : String(value)}
      </Typography>
    </Grid>
  );
};

Display.propTypes = {
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
};

Display.defaultProps = {
  value: null,
};

const YesNo = ({ label, value, classes }) => {
  return (
    <Grid item xs={6} sm={4} className={classes.field}>
      <Typography variant="caption" className={classes.label} display="block">
        {label}
      </Typography>
      <Typography variant="body2" className={value ? classes.yes : classes.no}>
        {value ? 'Oui' : 'Non'}
      </Typography>
    </Grid>
  );
};

YesNo.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
};

YesNo.defaultProps = {
  value: false,
};

const SectionTitle = ({ children, classes, first }) => {
  return (
    <Grid item xs={12}>
      <Typography
        variant="overline"
        className={`${classes.section} ${first ? classes.sectionFirst : ''}`}
      >
        {children}
      </Typography>
    </Grid>
  );
};

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  first: PropTypes.bool,
};

SectionTitle.defaultProps = {
  first: false,
};

const labelOf = (choices, id) => {
  const found = choices.find((c) => {
    return c.id === id;
  });
  return found ? found.name : id;
};

// cleaner_profile (plan 013 B2): 1 row per silae_id, edited inline. Reads/writes
// go straight through the data provider (cleaner_profile is keyed on silae_id,
// see ra-data-postgrest PRIMARY_KEYS), so we avoid react-admin id routing.
const ProfileCard = ({ silaeId, sourceCleanerId }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [profile, setProfile] = useState(null);
  const [exists, setExists] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    if (!silaeId) {
      setProfile(emptyProfile('', sourceCleanerId));
      setExists(false);
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    dataProvider
      .getList('cleaner_profile', {
        sort: { field: 'silae_id', order: 'ASC' },
        pagination: { page: 1, perPage: 1 },
        filter: { silae_id: silaeId },
      })
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          setProfile(data[0]);
          setExists(true);
        } else {
          setProfile(emptyProfile(silaeId, sourceCleanerId));
          setExists(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProfile(emptyProfile(silaeId, sourceCleanerId));
          setExists(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(reload, [silaeId, dataProvider, sourceCleanerId]);

  const openEdit = () => {
    setDraft({ ...(profile || emptyProfile(silaeId, sourceCleanerId)) });
    setOpen(true);
  };

  const setField = (source, value) => {
    setDraft((prev) => {
      return { ...prev, [source]: value };
    });
  };

  const handleNumeric = (source, raw) => {
    if (raw === '') {
      setField(source, null);
      return;
    }
    const n = Number(raw);
    setField(source, Number.isNaN(n) ? null : n);
  };

  const handleSave = async () => {
    if (!silaeId) {
      notify(
        'Matricule (silae_id) manquant — profil non enregistrable',
        'warning',
      );
      return;
    }
    setSaving(true);
    const payload = { ...draft, silae_id: silaeId };
    try {
      if (exists) {
        await dataProvider.update('cleaner_profile', {
          id: silaeId,
          data: payload,
          previousData: profile,
        });
      } else {
        await dataProvider.create('cleaner_profile', { data: payload });
      }
      notify('Profil vivier enregistré', 'info');
      setOpen(false);
      reload();
    } catch (err) {
      notify(err.message || "Échec de l'enregistrement du profil", 'warning');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid item xs={12} className={classes.root}>
      <Card>
        <CardHeader
          avatar={<AssignmentIndIcon color="action" />}
          title="Souhaits & profil"
          subheader="Attentes contractuelles et compétences transverses"
          action={
            <Button
              size="small"
              color="primary"
              startIcon={<EditIcon />}
              onClick={openEdit}
              disabled={!loaded || !silaeId}
            >
              Modifier
            </Button>
          }
        />
        <CardContent>
          {!silaeId && (
            <Typography variant="body2" color="textSecondary">
              Aucun matricule (silae_id) sur cette fiche — le profil vivier ne
              peut pas être rattaché.
            </Typography>
          )}
          {silaeId && !exists && loaded && (
            <Chip size="small" label="Profil non renseigné" />
          )}
          {silaeId && profile && (
            <Grid container spacing={1}>
              <SectionTitle classes={classes} first>
                Souhaits & disponibilité
              </SectionTitle>
              <Display
                label="Trajet domicile-site max (min)"
                value={profile.desired_commute_minutes}
                classes={classes}
              />
              <Display
                label="Heures/semaine minimum souhaitées"
                value={profile.min_weekly_hours_wish}
                classes={classes}
              />
              <Display
                label="Expérience dans le métier (années)"
                value={profile.experience_years}
                classes={classes}
              />
              <YesNo
                label="Ouvert à un CDD"
                value={profile.wants_cdd}
                classes={classes}
              />
              <YesNo
                label="Ouvert à un CDI"
                value={profile.wants_cdi}
                classes={classes}
              />

              <SectionTitle classes={classes}>
                Compétences transverses
              </SectionTitle>
              <Display
                label="Permis de conduire"
                value={
                  profile.driving_license
                    ? labelOf(DRIVING_LICENSE_CHOICES, profile.driving_license)
                    : null
                }
                classes={classes}
              />
              <Display
                label="Français parlé"
                value={
                  profile.french_spoken
                    ? labelOf(FRENCH_LEVEL_CHOICES, profile.french_spoken)
                    : null
                }
                classes={classes}
              />
              <Display
                label="Français écrit"
                value={
                  profile.french_written
                    ? labelOf(FRENCH_LEVEL_CHOICES, profile.french_written)
                    : null
                }
                classes={classes}
              />

              <SectionTitle classes={classes}>
                Aisance avec les e-mails
              </SectionTitle>
              <YesNo
                label="Sait ouvrir un e-mail"
                value={profile.email_can_open}
                classes={classes}
              />
              <YesNo
                label="Sait retrouver un e-mail"
                value={profile.email_can_find}
                classes={classes}
              />
              <YesNo
                label="Sait écrire un e-mail"
                value={profile.email_can_write}
                classes={classes}
              />

              <SectionTitle classes={classes}>
                Informations supplémentaires
              </SectionTitle>
              <YesNo
                label="Étudiant"
                value={profile.is_student}
                classes={classes}
              />
            </Grid>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => {
          return setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Souhaits & profil</DialogTitle>
        <DialogContent>
          {draft && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  className={classes.dialogSection}
                >
                  Souhaits & disponibilité
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Trajet domicile-site max"
                  type="number"
                  fullWidth
                  helperText="En minutes"
                  value={
                    draft.desired_commute_minutes == null
                      ? ''
                      : draft.desired_commute_minutes
                  }
                  onChange={(e) => {
                    return handleNumeric(
                      'desired_commute_minutes',
                      e.target.value,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Heures/semaine minimum"
                  type="number"
                  fullWidth
                  helperText="Volume horaire souhaité"
                  value={
                    draft.min_weekly_hours_wish == null
                      ? ''
                      : draft.min_weekly_hours_wish
                  }
                  onChange={(e) => {
                    return handleNumeric(
                      'min_weekly_hours_wish',
                      e.target.value,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Expérience dans le métier"
                  type="number"
                  fullWidth
                  helperText="En années"
                  value={
                    draft.experience_years == null ? '' : draft.experience_years
                  }
                  onChange={(e) => {
                    return handleNumeric('experience_years', e.target.value);
                  }}
                />
              </Grid>
              {[
                { source: 'wants_cdd', label: 'Ouvert à un CDD' },
                { source: 'wants_cdi', label: 'Ouvert à un CDI' },
              ].map((b) => {
                return (
                  <Grid item xs={6} key={b.source}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={!!draft[b.source]}
                          onChange={(e) => {
                            return setField(b.source, e.target.checked);
                          }}
                        />
                      }
                      label={b.label}
                    />
                  </Grid>
                );
              })}

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  className={classes.dialogSection}
                >
                  Compétences transverses
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Permis de conduire"
                  fullWidth
                  value={draft.driving_license || ''}
                  onChange={(e) => {
                    return setField('driving_license', e.target.value || null);
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {DRIVING_LICENSE_CHOICES.map((c) => {
                    return (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Français parlé"
                  fullWidth
                  value={draft.french_spoken || ''}
                  onChange={(e) => {
                    return setField('french_spoken', e.target.value || null);
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {FRENCH_LEVEL_CHOICES.map((c) => {
                    return (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Français écrit"
                  fullWidth
                  value={draft.french_written || ''}
                  onChange={(e) => {
                    return setField('french_written', e.target.value || null);
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {FRENCH_LEVEL_CHOICES.map((c) => {
                    return (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  className={classes.dialogSection}
                >
                  Aisance avec les e-mails
                </Typography>
                <Divider />
              </Grid>
              {[
                { source: 'email_can_open', label: 'Sait ouvrir un e-mail' },
                {
                  source: 'email_can_find',
                  label: 'Sait retrouver un e-mail',
                },
                { source: 'email_can_write', label: 'Sait écrire un e-mail' },
              ].map((b) => {
                return (
                  <Grid item xs={12} sm={4} key={b.source}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={!!draft[b.source]}
                          onChange={(e) => {
                            return setField(b.source, e.target.checked);
                          }}
                        />
                      }
                      label={b.label}
                    />
                  </Grid>
                );
              })}

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  className={classes.dialogSection}
                >
                  Informations supplémentaires
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={!!draft.is_student}
                      onChange={(e) => {
                        return setField('is_student', e.target.checked);
                      }}
                    />
                  }
                  label="Étudiant"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              return setOpen(false);
            }}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

ProfileCard.propTypes = {
  silaeId: PropTypes.string,
  sourceCleanerId: PropTypes.string,
};

ProfileCard.defaultProps = {
  silaeId: '',
  sourceCleanerId: '',
};

export default ProfileCard;
